import { useState } from 'react';
import { Alert, App as AntApp, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useCounterparties, useCreateCounterparty, useDeleteCounterparty, useUpdateCounterparty,
  type Counterparty, type CounterpartyType,
} from '../api/cash';
import { asApiError } from '@/shared/lib/api';

// 'customer' is created automatically from CRM customers — not manually selectable.
const TYPES: CounterpartyType[] = ['employee', 'supplier', 'partner', 'other'];

export function CounterpartiesPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCounterparties({ search: search.length >= 2 ? search : undefined });
  const create = useCreateCounterparty();
  const update = useUpdateCounterparty();
  const remove = useDeleteCounterparty();
  const [editing, setEditing] = useState<Counterparty | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  function openCreate() { setEditing(null); form.resetFields(); setOpen(true); }
  function openEdit(c: Counterparty) {
    setEditing(c);
    form.setFieldsValue({ name: c.name, type: c.type, phone: c.phone, note: c.note });
    setOpen(true);
  }

  async function onDelete(id: string) {
    try {
      await remove.mutateAsync(id);
      void message.success(t('kassa.counterpartyDeleted'));
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  async function onOk() {
    try {
      const v = await form.validateFields();
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: v });
        void message.success(t('kassa.counterpartyUpdated'));
      } else {
        await create.mutateAsync(v);
        void message.success(t('kassa.counterpartyCreated'));
      }
      setOpen(false);
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('kassa.counterparties')}</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('kassa.newCounterparty')}</Button>
      </div>

      <Alert type="info" showIcon message={t('kassa.counterpartiesHint')} />

      <Card bodyStyle={{ padding: 16 }}>
        <Input.Search allowClear placeholder={t('kassa.counterpartyPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 360 }} />
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table<Counterparty>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          pagination={false}
          columns={[
            { title: t('kassa.counterpartyName'), dataIndex: 'name' },
            { title: t('kassa.counterpartyType'), dataIndex: 'type', width: 150, render: (tp: CounterpartyType) => <Tag>{t(`kassa.counterpartyTypes.${tp}`)}</Tag> },
            { title: t('customers.phone'), dataIndex: 'phone', width: 160, render: (v: string | null) => v ?? '—' },
            { title: t('kassa.note'), dataIndex: 'note', ellipsis: true, render: (v: string | null) => v ?? '—' },
            {
              title: '', width: 96, render: (_: unknown, r: Counterparty) => (
                <Space size={4}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                  {r.type !== 'customer' && (
                    <Popconfirm
                      title={t('kassa.counterpartyDeleteConfirm')}
                      okText={t('common.delete')}
                      cancelText={t('common.cancel')}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onDelete(r.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? t('kassa.editCounterparty') : t('kassa.newCounterparty')}
        open={open}
        onOk={onOk}
        onCancel={() => { setOpen(false); form.resetFields(); }}
        confirmLoading={create.isPending || update.isPending}
        okText={editing ? t('common.save') : t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ type: 'other' }}>
          <Form.Item name="name" label={t('kassa.counterpartyName')} rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="type" label={t('kassa.counterpartyType')}>
            <Select options={TYPES.map((tp) => ({ value: tp, label: t(`kassa.counterpartyTypes.${tp}`) }))} />
          </Form.Item>
          <Form.Item name="phone" label={t('customers.phone')}><Input placeholder="+998..." /></Form.Item>
          <Form.Item name="note" label={t('kassa.note')}><Input.TextArea rows={2} maxLength={2000} /></Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
